import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';

function Vision1Requeue() {
  const { csrfToken, fetchCsrfToken, fetchWrapper } = useAuth();
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterText, setFilterText] = useState('');
  const [requeueing, setRequeueing] = useState(false);
  const [pipelineDisabled, setPipelineDisabled] = useState(false);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const rowSelection = useMemo(() => ({ mode: 'multiRow' }), []);

  const columnDefs = useMemo(() => [
    {
      headerName: '',
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      suppressMenu: true,
      pinned: 'left',
    },
    { field: 'created_at', headerName: 'Created', flex: 1, valueFormatter: (p) => p.value ? DateTime.fromISO(p.value).toLocaleString(DateTime.DATETIME_MED) : 'N/A' },
    { field: 'updated_at', headerName: 'Updated', flex: 1, valueFormatter: (p) => p.value ? DateTime.fromISO(p.value).toLocaleString(DateTime.DATETIME_MED) : 'N/A' },
    { field: 'business_name', headerName: 'Business', flex: 1.5, filter: 'agTextColumnFilter' },
    { field: 'sale_name', headerName: 'Sale', flex: 1.5, filter: 'agTextColumnFilter' },
    { field: 'file_name', headerName: 'File Name', flex: 1.5, filter: 'agTextColumnFilter' },
    { field: 'upload_log_id', headerName: 'Upload Log ID', width: 150 },
    { field: 'failed_stage', headerName: 'Stage', width: 110, filter: 'agTextColumnFilter' },
    { field: 'attempts', headerName: 'Attempts', width: 90 },
    { field: 'status', headerName: 'Status', width: 100, filter: 'agTextColumnFilter' },
    { field: 'last_error', headerName: 'Last Error', flex: 2 },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
  }), []);

  const fetchFailedUploads = async () => {
    setLoading(true);
    setError('');
    try {
      let currentCsrf = csrfToken;
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const res = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_failed_uploads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': currentCsrf },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Fetch failed_uploads: ${res.statusText}`);
      const data = await res.json();
      setRowData(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load failed uploads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFailedUploads();
    fetchPipelineFlag();
  }, []);

  const fetchPipelineFlag = async () => {
    setPipelineLoading(true);
    try {
      let currentCsrf = csrfToken;
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const res = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_system_flag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': currentCsrf },
        body: JSON.stringify({ p_key: 'pipeline_uploads_disabled' }),
      });
      if (!res.ok) throw new Error(`Fetch system flag failed: ${res.statusText}`);
      const value = await res.json();
      setPipelineDisabled(Boolean(value));
    } catch (err) {
      setError(err.message || 'Failed to load pipeline toggle');
    } finally {
      setPipelineLoading(false);
    }
  };

  const handleTogglePipeline = async () => {
    setPipelineLoading(true);
    setError('');
    try {
      let currentCsrf = csrfToken;
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const nextValue = !pipelineDisabled;
      const res = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/upsert_system_flag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': currentCsrf },
        body: JSON.stringify({ p_key: 'pipeline_uploads_disabled', p_value: nextValue }),
      });
      if (!res.ok) throw new Error(`Update system flag failed: ${res.statusText}`);
      setPipelineDisabled(nextValue);
    } catch (err) {
      setError(err.message || 'Failed to update pipeline toggle');
    } finally {
      setPipelineLoading(false);
    }
  };

  const handleRequeueSelected = async () => {
    const selected = gridRef.current?.api?.getSelectedRows() || [];
    console.log('[Vision1Requeue] Requeue clicked. Selected rows:', selected.length);
    if (!selected.length) {
      setError('Select one or more rows to requeue.');
      return;
    }
    setRequeueing(true);
    setError('');
    try {
      let currentCsrf = csrfToken;
      if (!currentCsrf) currentCsrf = await fetchCsrfToken();
      const upload_log_ids = selected.map(r => r.upload_log_id);
      console.log('[Vision1Requeue] Requeue request payload:', upload_log_ids);
      const res = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/failed_uploads_requeue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': currentCsrf },
        body: JSON.stringify({ upload_log_ids }),
      });
      console.log('[Vision1Requeue] Requeue response status:', res.status);
      if (!res.ok) throw new Error(`Requeue failed: ${res.statusText}`);
      const result = await res.json();
      console.log('[Vision1Requeue] Requeue response body:', result);
      await fetchFailedUploads();
    } catch (err) {
      setError(err.message || 'Requeue failed');
    } finally {
      setRequeueing(false);
    }
  };

  const handleBackToAdmin = () => {
    navigate('/admin');
  };

  const onFilterChange = (e) => {
    const value = e.target.value;
    setFilterText(value);
    gridRef.current?.api?.setGridOption('quickFilterText', value);
  };

  const onSelectionChanged = useCallback(() => {
    const count = gridRef.current?.api?.getSelectedRows()?.length || 0;
    setSelectedCount(count);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 p-4">
      <div className="flex justify-between mb-4 items-center">
        <h2 className="text-2xl font-bold text-gray-800">Vision1 Failed Uploads - Requeue</h2>
        <button
          onClick={handleBackToAdmin}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
        >
          Back to Admin
        </button>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-sm text-gray-600">Selected: {selectedCount}</span>
        <input
          type="text"
          value={filterText}
          onChange={onFilterChange}
          placeholder="Filter failed uploads..."
          className="w-full max-w-md border border-gray-300 rounded px-3 py-2"
        />
        <button
          onClick={handleTogglePipeline}
          disabled={pipelineLoading}
          className={`px-4 py-2 rounded-md text-white ${
            pipelineDisabled ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {pipelineLoading ? 'Updating...' : (pipelineDisabled ? 'Enable Uploads' : 'Disable Uploads')}
        </button>
        <button
          onClick={handleRequeueSelected}
          disabled={requeueing}
          className={`px-4 py-2 rounded-md text-white ${requeueing ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {requeueing ? 'Requeueing...' : 'Requeue Selected'}
        </button>
      </div>
      {loading && <p className="text-gray-500 text-center py-4">Loading...</p>}
      {error && <p className="text-red-500 mb-4 text-center bg-red-50 p-2 rounded">{error}</p>}
      <div style={{ height: 650, width: '100%' }}>
        <AgGridReact
          className="ag-theme-quartz"
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection={rowSelection}
          quickFilterText={filterText}
          pagination={true}
          paginationPageSize={25}
          paginationPageSizeSelector={[25, 50, 100]}
          animateRows={true}
          suppressRowClickSelection={false}
          onSelectionChanged={onSelectionChanged}
        />
      </div>
    </div>
  );
}

export default Vision1Requeue;
